import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { ThumbsUp, ThumbsDown, TrendingUp, Users, BarChart3 } from 'lucide-react';

interface EvaluationStats {
  ruta: number;
  totalEvaluations: number;
  likes: number;
  dislikes: number;
  approvalRate: number;
  totalAmount: number;
}

interface EvaluationDetail {
  id: string;
  client_name: string;
  cpf: string;
  evaluation_type: 'like' | 'dislike';
  approval_status: 'approved' | 'rejected';
  loan_amount: number;
  ruta: number;
  evaluated_at: string;
  seller: {
    email: string;
  } | null;
}

export default function ContractEvaluations() {
  const [stats, setStats] = useState<EvaluationStats[]>([]);
  const [details, setDetails] = useState<EvaluationDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const [selectedRuta, setSelectedRuta] = useState<number>(0);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('0');

  useEffect(() => {
    fetchEvaluationData();
  }, [selectedPeriod, user]);

  const fetchEvaluationData = async () => {
    if (!user) return;
    
    try {
      const today = new Date();
      let startDate: Date;
      let endDate: Date;

      const monthsAgo = parseInt(selectedPeriod);

      // Calculate start date (first day of the target month)
      startDate = new Date(today.getFullYear(), today.getMonth() - monthsAgo, 1);
      startDate.setHours(0, 0, 0, 0);

      if (monthsAgo === 0) {
        // For current month, end date is current date and time
        endDate = new Date();
      } else {
        // For past months, end date is the last day of that month
        endDate = new Date(today.getFullYear(), today.getMonth() - monthsAgo + 1, 0);
        endDate.setHours(23, 59, 59, 999);
      }

      // Fetch evaluation statistics
      const { data: evaluationsData, error: evaluationsError } = await supabase
        .from('contract_evaluations')
        .select(`
          *,
          seller:seller_id(email)
        `)
        .gte('evaluated_at', startDate.toISOString())
        .lte('evaluated_at', endDate.toISOString())
        .eq('unit_id', user?.unit_id)
        .order('evaluated_at', { ascending: false });

      if (evaluationsError) throw evaluationsError;

      // Group by ruta and calculate statistics
      const rutaStats: { [key: number]: EvaluationStats } = {};

      evaluationsData?.forEach(evaluation => {
        const ruta = evaluation.ruta;
        
        if (!rutaStats[ruta]) {
          rutaStats[ruta] = {
            ruta,
            totalEvaluations: 0,
            likes: 0,
            dislikes: 0,
            approvalRate: 0,
            totalAmount: 0
          };
        }

        rutaStats[ruta].totalEvaluations += 1;
        rutaStats[ruta].totalAmount += evaluation.loan_amount;

        if (evaluation.evaluation_type === 'like') {
          rutaStats[ruta].likes += 1;
        } else {
          rutaStats[ruta].dislikes += 1;
        }
      });

      // Calculate approval rates
      Object.values(rutaStats).forEach(stat => {
        stat.approvalRate = stat.totalEvaluations > 0 
          ? (stat.likes / stat.totalEvaluations) * 100 
          : 0;
      });

      setStats(Object.values(rutaStats).sort((a, b) => a.ruta - b.ruta));
      setDetails(evaluationsData || []);
    } catch (err) {
      console.error('Error fetching evaluation data:', err);
      setError('Erro ao carregar dados de avaliação');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredDetails = () => {
    if (selectedRuta === 0) return details;
    return details.filter(detail => detail.ruta === selectedRuta);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatCPF = (cpf: string) => {
    const numbers = cpf.replace(/\D/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const getOverallStats = () => {
    const totalEvaluations = stats.reduce((sum, stat) => sum + stat.totalEvaluations, 0);
    const totalLikes = stats.reduce((sum, stat) => sum + stat.likes, 0);
    const totalDislikes = stats.reduce((sum, stat) => sum + stat.dislikes, 0);
    const overallApprovalRate = totalEvaluations > 0 ? (totalLikes / totalEvaluations) * 100 : 0;
    const totalAmount = stats.reduce((sum, stat) => sum + stat.totalAmount, 0);

    return {
      totalEvaluations,
      totalLikes,
      totalDislikes,
      overallApprovalRate,
      totalAmount
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        <p>{error}</p>
      </div>
    );
  }

  const overallStats = getOverallStats();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Avaliações de Contratos</h1>
        <div className="flex items-center gap-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            <option value="0">Mês Atual</option>
            <option value="1">Mês Anterior</option>
            <option value="2">2 Meses Atrás</option>
            <option value="3">3 Meses Atrás</option>
            <option value="6">6 Meses Atrás</option>
            <option value="12">12 Meses Atrás</option>
          </select>
        </div>
      </div>

      {/* Overall Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <BarChart3 className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Avaliações</p>
              <p className="text-2xl font-bold text-gray-900">{overallStats.totalEvaluations}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <ThumbsUp className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Likes (Aprovados)</p>
              <p className="text-2xl font-bold text-green-600">{overallStats.totalLikes}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 text-red-600">
              <ThumbsDown className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Dislikes (Recusados)</p>
              <p className="text-2xl font-bold text-red-600">{overallStats.totalDislikes}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Taxa de Aprovação</p>
              <p className="text-2xl font-bold text-purple-600">{overallStats.overallApprovalRate.toFixed(1)}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics by Ruta */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Estatísticas por Ruta</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ruta
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total de Avaliações
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Likes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dislikes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Taxa de Aprovação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stats.map((stat) => (
                <tr key={stat.ruta} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">Ruta {stat.ruta}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{stat.totalEvaluations}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <ThumbsUp className="w-4 h-4 text-green-600 mr-2" />
                      <span className="text-sm text-green-600 font-medium">{stat.likes}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <ThumbsDown className="w-4 h-4 text-red-600 mr-2" />
                      <span className="text-sm text-red-600 font-medium">{stat.dislikes}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${stat.approvalRate}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-900 font-medium">
                        {stat.approvalRate.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCurrency(stat.totalAmount)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detailed Evaluations */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Detalhes das Avaliações</h2>
          <select
            value={selectedRuta}
            onChange={(e) => setSelectedRuta(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            <option value={0}>Todas as Rutas</option>
            <option value={1}>Ruta 1</option>
            <option value={2}>Ruta 2</option>
            <option value={3}>Ruta 3</option>
            <option value={4}>Ruta 4</option>
            <option value={5}>Ruta 5</option>
            <option value={6}>Ruta 6</option>
            <option value={7}>Ruta 7</option>
            <option value={8}>Ruta 8</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CPF
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ruta
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avaliação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendedor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getFilteredDetails().map((detail) => (
                <tr key={detail.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{detail.client_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCPF(detail.cpf)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">Ruta {detail.ruta}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {detail.evaluation_type === 'like' ? (
                        <>
                          <ThumbsUp className="w-4 h-4 text-green-600 mr-2" />
                          <span className="text-sm text-green-600 font-medium">Like</span>
                        </>
                      ) : (
                        <>
                          <ThumbsDown className="w-4 h-4 text-red-600 mr-2" />
                          <span className="text-sm text-red-600 font-medium">Dislike</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      detail.approval_status === 'approved' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {detail.approval_status === 'approved' ? 'Aprovado' : 'Recusado'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCurrency(detail.loan_amount)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{detail.seller?.email || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(detail.evaluated_at).toLocaleDateString('pt-BR')}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {getFilteredDetails().length === 0 && (
          <div className="text-center text-gray-500 py-8">
            Nenhuma avaliação encontrada para o período selecionado.
          </div>
        )}
      </div>
    </div>
  );
}